import React, { useState, useRef } from "react";
import { useSelector } from "react-redux";
import {
  Send,
  Loader2,
  Paperclip,
  X,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
  ShieldCheck,
  FileText,
} from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "components/ui/card";
import { Button } from "components/ui/button";
import { toast } from "components/ui/toast";
import { submitSupportTicket } from "Explore/Services/Help";
import { SUPPORT_CATEGORIES, PRIORITY_OPTIONS } from "../../helpContent";

const MAX_FILE_SIZE_MB = 5;
const ALLOWED_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "application/pdf",
  "text/plain",
  "application/json",
];

const ContactSupport = () => {
  const user = useSelector((state) => state.user?.data);
  const fileInputRef = useRef(null);

  // Form states
  const [category, setCategory] = useState("technical");
  const [priority, setPriority] = useState("medium");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedTicket, setSubmittedTicket] = useState(null);

  // Handle file attachment
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      toast.error("File format not supported. Please upload PNG, JPG, WebP, PDF, JSON, or TXT.", {
        title: "Invalid File Format",
      });
      e.target.value = "";
      return;
    }

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      toast.error(`File size exceeds the ${MAX_FILE_SIZE_MB}MB limit.`, {
        title: "File Too Large",
      });
      e.target.value = "";
      return;
    }

    setSelectedFile(file);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side validations
    if (!subject.trim()) {
      toast.error("Please enter a subject for your support request.", {
        title: "Missing Subject",
      });
      return;
    }

    if (!message.trim() || message.trim().length < 10) {
      toast.error("Please describe your issue in at least 10 characters.", {
        title: "Description Too Short",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await submitSupportTicket({
        category,
        priority,
        subject: subject.trim(),
        message: message.trim(),
        file: selectedFile,
        page_url: window.location.href,
        user_agent: navigator.userAgent,
      });

      const ticketId = response?.ticket_id || `TCK-${Date.now().toString().slice(-6)}`;
      setSubmittedTicket({
        id: ticketId,
        message: response?.message || "Your support request has been submitted successfully.",
      });

      toast.success(`Support ticket ${ticketId} created. We will get back to you shortly.`, {
        title: "Ticket Submitted",
      });

      // Clear fields
      setSubject("");
      setMessage("");
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (err) {
      console.error("Error submitting support ticket:", err);
      const errorMsg =
        err.response?.data?.message || err.message || "Failed to submit support request. Please try again later.";
      toast.error(errorMsg, {
        title: "Submission Failed",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact-support" className="space-y-4 pt-4">
      <Card className="border-gray-200 shadow-sm bg-white overflow-hidden">
        <CardHeader className="border-b border-gray-100 bg-gray-50/50 pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <CardTitle className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <span>Contact Academic Online Support</span>
              </CardTitle>
              <CardDescription className="text-xs text-gray-500 mt-0.5">
                Have a technical question, encounter an issue, or need help with custom data exports?
              </CardDescription>
            </div>
            <div className="flex items-center gap-1.5 text-[11px] text-gray-500 bg-white px-2.5 py-1 rounded-md border border-gray-200 shrink-0">
              <ShieldCheck size={14} className="text-emerald-500" />
              <span>Priority Response for Subscribed Users</span>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-6">
          {submittedTicket ? (
            <div className="py-8 text-center space-y-4 max-w-lg mx-auto">
              <div className="w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto">
                <CheckCircle2 size={32} />
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-gray-900">Support Ticket Created</h3>
                <p className="text-sm font-mono text-blue-600 font-semibold bg-blue-50 py-1 px-3 rounded-md inline-block">
                  Reference: #{submittedTicket.id}
                </p>
                <p className="text-xs text-gray-500 leading-relaxed">
                  {submittedTicket.message} A confirmation has been registered for{" "}
                  <strong className="text-gray-700">{user?.email || "your account"}</strong>.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSubmittedTicket(null)}
                className="text-xs mt-2"
              >
                Submit Another Request
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* User Identity Banner */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-200 text-xs text-gray-600">
                <div className="flex items-center gap-2 truncate">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                  <span className="truncate">
                    Submitting as: <strong className="text-gray-900">{user?.full_name || user?.name || "User"}</strong> (
                    {user?.email || "user@example.com"})
                  </span>
                </div>
                <span className="text-[11px] text-gray-400 shrink-0 hidden sm:inline">Academic Online Support</span>
              </div>

              {/* Category & Priority Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-gray-700">Issue Category</label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full text-xs sm:text-sm px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {SUPPORT_CATEGORIES.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-gray-700">Priority Level</label>
                  <select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    className="w-full text-xs sm:text-sm px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {PRIORITY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Subject */}
              <div className="space-y-1.5">
                <div className="flex justify-between items-center">
                  <label className="text-xs font-semibold text-gray-700">Subject</label>
                  <span className="text-[11px] text-gray-400">{subject.length}/120</span>
                </div>
                <input
                  type="text"
                  maxLength={120}
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Brief summary of the issue (e.g. Export job failing on CS department)"
                  className="w-full text-xs sm:text-sm px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400"
                  required
                />
              </div>

              {/* Message Details */}
              <div className="space-y-1.5">
                <div className="flex justify-between items-center">
                  <label className="text-xs font-semibold text-gray-700">Detailed Message</label>
                  <span className="text-[11px] text-gray-400">{message.length} characters (min 10)</span>
                </div>
                <textarea
                  rows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Please describe the steps to reproduce, expected results, or error messages encountered..."
                  className="w-full text-xs sm:text-sm px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400 leading-relaxed"
                  required
                />
              </div>

              {/* File Attachment & Metadata */}
              <div className="space-y-2">
                <label className="text-xs font-semibold text-gray-700 flex items-center justify-between">
                  <span>Attachment (Optional)</span>
                  <span className="text-[11px] text-gray-400 font-normal">Max 5MB (PNG, JPG, PDF, TXT)</span>
                </label>

                <div className="flex items-center gap-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileChange}
                    accept=".png,.jpg,.jpeg,.webp,.pdf,.txt,.json"
                    className="hidden"
                    id="support-attachment-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    className="text-xs h-8 gap-1.5 text-gray-600"
                  >
                    <Paperclip size={14} />
                    <span>Choose File</span>
                  </Button>

                  {selectedFile ? (
                    <div className="flex items-center gap-2 px-2.5 py-1 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-700">
                      <FileText size={14} />
                      <span className="truncate max-w-[200px]">{selectedFile.name}</span>
                      <span className="text-[10px] text-blue-500">
                        ({(selectedFile.size / 1024).toFixed(1)} KB)
                      </span>
                      <button
                        type="button"
                        onClick={handleRemoveFile}
                        className="text-blue-500 hover:text-blue-900 p-0.5 rounded"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400">No file chosen</span>
                  )}
                </div>
              </div>

              {/* Diagnostic metadata info note */}
              <div className="p-3 rounded-lg bg-gray-50 border border-gray-200 flex items-start gap-2.5 text-xs text-gray-500">
                <AlertCircle size={15} className="text-gray-400 shrink-0 mt-0.5" />
                <div className="space-y-0.5 leading-normal">
                  <p>
                    Technical diagnostic data (such as browser version and current URL) will be automatically included
                    to help our support engineers diagnose issues faster.
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-3 pt-2">
                <Button
                  type="submit"
                  disabled={isSubmitting || !subject.trim() || message.trim().length < 10}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm px-5 h-9 gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={15} className="animate-spin" />
                      <span>Submitting Ticket...</span>
                    </>
                  ) : (
                    <>
                      <Send size={15} />
                      <span>Send Support Ticket</span>
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </section>
  );
};

export default ContactSupport;
